import React, { Component } from "react";
import AppRouter from "./components/AppRouter";
import UserContext from "./context/UserContext";
import { getUser } from "./network/user";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Helmet } from "react-helmet";
import { Crisp } from "crisp-sdk-web";
import { LanguageProvider } from "context/LanguageProvider";
import { clearSanctumToken } from "./lib/auth-token";
import RateLimitNotifier from "./components/common/RateLimitNotifier";
import DateLocalizationProvider from "./components/common/DateLocalizationProvider";

import axios from "axios";
axios.defaults.withCredentials = true;

function parseStoredJson(key) {
  const saved = localStorage.getItem(key);
  if (!saved) return null;

  try {
    return JSON.parse(saved);
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

class App extends Component {
  constructor(props) {
    super(props);

    const parsedOrganization = parseStoredJson("selectedOrganization");
    const parsedGestionCustomer = parseStoredJson("selectedGestionCustomer");

    this.changeUser = (user, callback) => {
      const isLoggedIn = !!user;
      const validOrg =
        parsedOrganization && user?.organizations?.some((org) => org.id === parsedOrganization.id)
          ? parsedOrganization
          : user?.organizations?.[0] || null;

      const validGestionCustomer =
        parsedGestionCustomer && user
          ? parsedGestionCustomer
          : null;

      const newState = {
        user: user,
        loggedIn: isLoggedIn,
        selectedOrganization: validOrg,
        selectedGestionCustomer: validGestionCustomer,
        gestionCustomers: isLoggedIn ? this.state.gestionCustomers : [],
        gestionCustomersLoaded: isLoggedIn ? this.state.gestionCustomersLoaded : false,
      };

      if (validOrg) {
        localStorage.setItem("selectedOrganization", JSON.stringify(validOrg));
      } else {
        localStorage.removeItem("selectedOrganization");
      }
      if (!isLoggedIn) {
        localStorage.removeItem("selectedGestionCustomer");
      }

      this.setState(newState, callback);
    };

    this.setSelectedOrganization = (organization, callback) => {
      const newState = { ...this.state, selectedOrganization: organization };
      localStorage.setItem("selectedOrganization", JSON.stringify(organization));
      this.setState(newState, callback);
    };

    this.setSelectedGestionCustomer = (customer, callback) => {
      const newState = { ...this.state, selectedGestionCustomer: customer };
      if (customer) {
        localStorage.setItem("selectedGestionCustomer", JSON.stringify(customer));
      } else {
        localStorage.removeItem("selectedGestionCustomer");
      }
      this.setState(newState, callback);
    };

    this.setGestionCustomers = (customers, callback) => {
      const list = customers ?? [];
      const validSelection =
        this.state.selectedGestionCustomer &&
        list.some((customer) => customer.id === this.state.selectedGestionCustomer.id)
          ? this.state.selectedGestionCustomer
          : list[0] ?? null;

      const newState = {
        ...this.state,
        gestionCustomers: list,
        gestionCustomersLoaded: true,
        selectedGestionCustomer: validSelection,
      };

      if (validSelection) {
        localStorage.setItem("selectedGestionCustomer", JSON.stringify(validSelection));
      }

      this.setState(newState, callback);
    };

    this.state = {
      user: null,
      selectedOrganization: parsedOrganization,
      selectedGestionCustomer: parsedGestionCustomer,
      gestionCustomers: [],
      gestionCustomersLoaded: false,
      loggedIn: false,
      changeUser: this.changeUser,
      setSelectedOrganization: this.setSelectedOrganization,
      setSelectedGestionCustomer: this.setSelectedGestionCustomer,
      setGestionCustomers: this.setGestionCustomers,
    };

    Crisp.configure("f2aebf52-501c-4b84-aba7-a2ff4158ebc4", {
      autoload: false,
    });
  }

  componentDidMount() {
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("password");

    getUser()
      .then((response) => {
        const user = response.data.data;

        this.changeUser(user, () => {
          this.setState({ userReady: true });
        });

        // Crisp User Info
        Crisp.load();
        Crisp.user.setEmail(user.email);
        Crisp.user.setPhone(user.cellphone_number);
        Crisp.user.setNickname(user.full_name);
        Crisp.session.setData({
          user_id: user.id,
          user_email: user.email,
          user_name: user.full_name,
          user_role: user.role,
        });
      })
      .catch(() => {
        localStorage.removeItem("selectedOrganization");
        localStorage.removeItem("selectedGestionCustomer");
        clearSanctumToken();
        this.setState({
          user: null,
          userReady: true,
          loggedIn: false,
          selectedOrganization: null,
          gestionCustomers: [],
          gestionCustomersLoaded: false,
          selectedGestionCustomer: null,
        });
      });
  }

  render() {
    return (
      <LanguageProvider>
        <DateLocalizationProvider>
          <RateLimitNotifier />
          <DndProvider backend={HTML5Backend}>
            <Helmet>
              <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
            </Helmet>

            <UserContext value={this.state}>
              <AppRouter {...this.props} />
            </UserContext>
          </DndProvider>
        </DateLocalizationProvider>
      </LanguageProvider>
    );
  }
}

export default App;
